import React, { useMemo, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { FilterLayoutToggle, FiltersShowAction } from '@pkg/components/filters';
import { FieldSearch, type FieldSearchProps } from '@pkg/components/forms';
import { type Collectible } from '@pkg/graphql/codegen/graphql';
import { useAppSelector } from '@pkg/hooks/redux/useAppSelector.ts';

import { useNFTsFiltersStore, useNFTsPageStore } from '@app/store/zustand';

import { NftFiltersMobileSheet } from '../NftFiltersMobileSheet/NftFiltersMobileSheet.tsx';
import { NftFiltersReport } from '../NftFiltersReport/NftFiltersReport.tsx';

import { FixedBottom, MobileWrapper, Row, SearchWrapper, SelectAllAction } from './NftFiltersPrimaryMobile.style.ts';

export interface NftFiltersPrimaryMobileProps {
    nftEntries?: Collectible[];
}

export function NftFiltersPrimaryMobile({ nftEntries }: NftFiltersPrimaryMobileProps) {
    const [open, setOpen] = useState(false);

    const isPageOwner = useAppSelector((state) => state.yatPage.isPageOwner);
    const { marketplaceV1Enabled } = useAppSelector((state) => state.featureFlags.features);

    const { isListView, setIsListView, isSelectAll, handleSelectAll } = useNFTsPageStore();
    const currentFilters = useNFTsFiltersStore((state) => state.currentFilters);
    const search = useNFTsFiltersStore((state) => state.search);
    const setSearch = useNFTsFiltersStore((state) => state.setSearch);

    // TODO: Is it beneficial to use a debounce / useDeferredValue?
    const handleSearch: FieldSearchProps['onChange'] = (newValue = '') => {
        const stringified = newValue.toString();
        setSearch(stringified);
    };

    const showReport = useMemo(() => {
        return Boolean(currentFilters.total);
    }, [currentFilters]);

    // Consider animating this.
    const ownerFiltersMarkup =
        marketplaceV1Enabled && isPageOwner ? (
            <FixedBottom>
                <FilterLayoutToggle selected={isListView} onSelected={setIsListView} />

                <SelectAllAction onAction={() => handleSelectAll(nftEntries || [])}>
                    {isSelectAll ? 'Select None' : 'Select All'}
                </SelectAllAction>
            </FixedBottom>
        ) : null;

    const reportMarkup = showReport ? (
        <NftFiltersReport key="NftFiltersReport-Mobile" resultsCount={nftEntries?.length} />
    ) : null;

    const mobileSheetMarkup = open ? (
        <NftFiltersMobileSheet key="NftFiltersMobileSheet" onClose={() => setOpen(false)} />
    ) : null;

    return (
        <MobileWrapper>
            <Row>
                <SearchWrapper>
                    <FieldSearch value={search} placeholder="Search collection or token" onChange={handleSearch} />
                </SearchWrapper>

                <FiltersShowAction iconOnly pressed={open} onToggle={setOpen} />
            </Row>

            <AnimatePresence>
                {reportMarkup}
                {mobileSheetMarkup}
            </AnimatePresence>

            {ownerFiltersMarkup}
        </MobileWrapper>
    );
}
